import { useCallback, useEffect, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ClipboardCheck, FileText, FolderOpen, History, LogIn, RotateCcw, Settings } from "lucide-react";
import { useRuntime } from "./hooks/useRuntime";
import SourcePickerDialog from "./components/document/SourcePickerDialog";
import Dashboard from "./pages/Dashboard";
import Documents, { ADMIN_SECTIONS } from "./pages/Documents";
import Login from "./pages/Login";
import ReportGeneration from "./pages/ReportGeneration";
import Review from "./pages/Review";
import WorkspaceRoutes from "./workspace/routes";

function NotFound() {
  return <main className="page" role="alert"><h1>404</h1><p>Page introuvable.</p><Link className="btn" to="/">Retour</Link></main>;
}

export default function App() {
  const { pathname } = useLocation();
  return pathname === "/" || pathname.startsWith("/dossiers") || pathname.startsWith("/contribute/") || pathname === "/login" ? <WorkspaceRoutes /> : <PrototypeApp />;
}

// The pre-workspace prototype (plan review, block production on demo data,
// audits, admin). Kept working as-is under its own routes.
function PrototypeApp() {
  const { pathname } = useLocation();
  const routerNavigate = useNavigate();
  const { runtime, error } = useRuntime();
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const navigate = useCallback((path: string) => { routerNavigate(path || "/"); refresh(); }, [routerNavigate, refresh]);

  const reset = useCallback(() => {
    if (!runtime) return;
    runtime.agents.clearAllRuns();
    runtime.report.closeDrawer();
    runtime.state.resetDemo();
    runtime.ui.showToast(runtime.i18n.t("toast.demoReset"));
    navigate("/production");
  }, [runtime, navigate]);

  useEffect(() => {
    window.addEventListener("sfcr:render", refresh);
    window.addEventListener("sfcr:reset", reset);
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") runtime?.report.closeDrawer();
    };
    document.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("sfcr:render", refresh);
      window.removeEventListener("sfcr:reset", reset);
      document.removeEventListener("keydown", escape);
    };
  }, [runtime, refresh, reset]);

  if (error) return <main className="page" role="alert"><p>{error}</p><button className="btn" onClick={() => window.location.reload()}>Réessayer</button></main>;
  if (!runtime) return <main className="page" role="status">Chargement…</main>;

  const { t, getLocale, setLocale, LOCALES } = runtime.i18n;
  const admin = pathname.startsWith("/admin");
  const plan = runtime.state.getState().phase === "plan_review";
  const links = [
    { path: "/", label: t("nav.workspace"), Icon: FolderOpen },
    { path: "/production", label: t(plan ? "nav.plan" : "nav.report"), Icon: FileText },
    { path: "/audits", label: t("nav.audits"), Icon: ClipboardCheck },
    { path: "/timeline", label: t("nav.timeline"), Icon: History },
  ];
  const adminPage = <Documents runtime={runtime} path={pathname} revision={revision}
    navigate={navigate} refresh={refresh} />;

  return <div className="shell">
    {!admin && <header className="topbar">
      <Link className="brand" to="/production"><img src="/seabird-tech-logo.svg" alt="Seabird Tech" /><span className="brand-copy"><strong>{t("brand.title")}</strong><span>{t("brand.subtitle")}</span></span></Link>
      <nav className="mainnav">{links.map(({ path, label, Icon }) => <Link key={path} to={path} className={`navlink ${pathname === path ? "active" : ""}`}><Icon size={16} />{label}</Link>)}</nav>
      <div className="top-actions">
        <div className="lang-switch" role="group" aria-label={t("lang.switchLabel")}>{LOCALES.map(locale => <button type="button" key={locale} data-lang={locale} className={`lang-btn ${getLocale() === locale ? "active" : ""}`} onClick={() => setLocale(locale)}>{locale.toUpperCase()}</button>)}</div>
        <button className="tiny-btn" onClick={reset} title={t("action.reset")} aria-label={t("action.reset")}><RotateCcw size={16} /></button>
        <Link className="tiny-btn admin-btn" to="/admin" title={t("action.admin")}><Settings size={16} />{t("action.admin")}</Link>
        <Link className="icon-btn" to="/login" title={t("login.title")} aria-label={t("login.title")}><LogIn size={16} /></Link>
      </div>
    </header>}
    <Routes>
      <Route path="/production" element={<ReportGeneration runtime={runtime} path={pathname} revision={revision} navigate={navigate} />} />
      <Route path="/audits" element={<Review runtime={runtime} navigate={navigate} />} />
      <Route path="/timeline" element={<Dashboard runtime={runtime} />} />
      <Route path="/login" element={<Login runtime={runtime} navigate={navigate} />} />
      <Route path="/admin" element={adminPage} />
      {ADMIN_SECTIONS.map(section => <Route key={section} path={`/admin/${section}`} element={adminPage} />)}
      {ADMIN_SECTIONS.map(section => <Route key={`${section}-detail`} path={`/admin/${section}/:detailId`} element={adminPage} />)}
      <Route path="*" element={<NotFound />} />
    </Routes>
    <SourcePickerDialog documents={runtime.state.getState().kbDocuments} onSaved={refresh} />
  </div>;
}
